import React, { useState, useEffect, useRef, useMemo, useContext } from "react";
import { Helmet } from "react-helmet";
import BackButton from "../../../assets/image/BackButton";
import { Link, useNavigate } from "react-router-dom";
import { getApi } from "../../../utils/services";
import moment from "moment";
import Loader from "../../common/Loader";
import { OverlayTrigger, Tooltip, Form } from "react-bootstrap";
import { useTable, useSortBy } from "react-table";
import exporticon from "../../../assets/image/Excel_Download_Icon.png";
import { fetchPettyCashFunds } from "../../../utils/helpers/globalMethod";
import AddFundModal from "../../modal/AddFundModal";
import { VP_ID } from "../../../utils/constant";
import { VIEW_PETTYCASH_BILL } from "../../../utils/apiRoutes";
import { ModalContext } from "../../../utils/Context";
import CreateBillIcon from "../../../assets/image/CreateBillIcon";
import ExportFundModal from "../../modal/ExportFundModal";
import Alert from "../../modal/Alert";

const PettyCashFunds = () => {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem("userData"));
    const expenseHistoryRef = useRef(null);

    const [fundsList, setFundsList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedFund, setSelectedFund] = useState(null);
    const [expenseHistoryLoading, setExpenseHistoryLoading] = useState(false);
    const [selectedFundNo, setSelectedFundNo] = useState(null);
    const [fundFilter, setFundFilter] = useState("");
    const [neededFund, setNeededFund] = useState(0);
    const [showNeededFund, setShowNeededFund] = useState(false);
    const [totalRemainingFund, setTotalRemainingFund] = useState(0);
    const modalContext = useContext(ModalContext);
    const { handleModalData } = modalContext || {};

    useEffect(() => {
        fetchFundsList();
    }, []);

    const fetchFundsList = async () => {
        setLoading(true);
        const {
            allFunds,
            validFunds,
            totalRemaining,
            totalNeeded
        } = await fetchPettyCashFunds();

        setFundsList(allFunds);
        setTotalRemainingFund(totalRemaining);

        if (totalNeeded > 0) {
            setNeededFund(totalNeeded);
            setShowNeededFund(true);
        } else {
            setShowNeededFund(false);
        }

        setLoading(false);
    };

    const fetchFirstInvoiceDetails = async (invoiceId) => {
        try {
            const result = await getApi(`${VIEW_PETTYCASH_BILL}/${invoiceId}`);
            return result.res.pettyCash_details;
        } catch (error) {
            console.error("Error fetching invoice details:", error);
            return null;
        }
    };

    const handleAddFundClick = () => {
        if (typeof handleModalData === 'function') {
            const addFundModal = (
                <AddFundModal
                    show={true}
                    onHide={() => handleModalData(null)}
                    fetchFundsList={fetchFundsList}
                    fundsList={fundsList}
                />
            );
            handleModalData(addFundModal, "md");
        } else {
            console.error("handleModalData is not available");
        }
    };

    const handleExportClick = () => {
        if (fundsList.length > 0 && typeof handleModalData === 'function') {
            const exportModal = (
                <ExportFundModal
                    fundsList={fundsList}
                    onHide={() => handleModalData(null)}
                    fetchFirstInvoiceDetails={fetchFirstInvoiceDetails}
                />
            );
            handleModalData(exportModal, "md");
        }
        else {
            const alert = <Alert message="No funds available to export." />;
            handleModalData(alert, "md");
        }
    };


    const handleFundFilterChange = (e) => {
        setFundFilter(e.target.value);
        setSelectedFund(null);
        setSelectedFundNo(null);
    };

    const handleFundClick = async (fund) => {
        setSelectedFundNo(fund.FundNo);
        setExpenseHistoryLoading(true);
        setSelectedFund(fund);
        setTimeout(() => {
            expenseHistoryRef.current?.scrollIntoView({ behavior: 'smooth' });
            setExpenseHistoryLoading(false);
        }, 100);
    };

    const filteredFunds = useMemo(() => {
        if (!fundFilter) return fundsList;
        return fundsList.filter(fund => fund.FundNo === fundFilter);
    }, [fundsList, fundFilter]);

    const totalAmount = fundsList.reduce((sum, fund) => sum + parseFloat(fund.FundAmount), 0);
    const balanceAmount = fundsList.reduce((sum, fund) => sum + parseFloat(fund.RemainingAmount), 0);

    const columns = useMemo(
        () => [
            {
                Header: "Fund No.",
                accessor: "FundNo",
                Cell: ({ row }) => {
                    const fund = row.original;
                    return fund.Invoices && fund.Invoices.length > 0 ? (
                        <span
                            className='cursor-pointer text-primary'
                            onClick={() => handleFundClick(fund)}
                            style={selectedFundNo === fund.FundNo ? { fontWeight: 'bold', textDecoration: 'underline' } : { color: '#07efd' }}
                        >
                            {fund.FundNo}
                        </span>
                    ) : (
                        <span>{fund.FundNo}</span>
                    );
                }
            },
            {
                Header: "Created Date",
                accessor: "CreatedAt",
                Cell: ({ value }) => moment(value).format("DD-MM-YYYY")
            },
            {
                Header: "Description",
                accessor: "Description",
                Cell: ({ value }) => value ? (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id="tooltip1">{value}</Tooltip>}
                    >
                        <div className="truncate-error-text">{value}</div>
                    </OverlayTrigger>
                ) : (
                    <div className="truncate-error-text">N/A</div>
                ),
                disableSortBy: true
            },
            {
                Header: <div className='ms-auto'>Fund Amount</div>,
                accessor: "FundAmount",
                Cell: ({ value }) => <><div className='text-end'>{`₹ ${parseFloat(value).toFixed(2)}`}</div></>,
                sortType: (a, b) => {
                    const numA = parseFloat(a.original.FundAmount);
                    const numB = parseFloat(b.original.FundAmount);
                    return numA > numB ? 1 : -1;
                }
            },
            {
                Header: <div className='ms-auto'>Balance Amount</div>,
                accessor: "RemainingAmount",
                Cell: ({ value }) => <><div className='text-end'>{`₹ ${parseFloat(value).toFixed(2)}`}</div></>,
                sortType: (a, b) => {
                    const numA = parseFloat(a.original.RemainingAmount);
                    const numB = parseFloat(b.original.RemainingAmount);
                    return numA > numB ? 1 : -1;
                }
            }
        ],
        [selectedFundNo]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable(
        {
            columns,
            data: filteredFunds,
        },
        useSortBy
    );

    return (
        <>
            <Helmet>
                <title>Vendor Portal || Petty Cash Funds</title>
            </Helmet>
            <Loader isLoading={loading} />

            <div className="container-section">
                <div className="section-header d-flex justify-content-between align-items-center gap-2 flex-wrap">
                    <div className="d-flex align-items-center">
                        <button onClick={() => navigate(-1)} className="back-viewbtn me-2">
                            <BackButton />
                        </button>
                        <h3>Petty Cash Funds</h3>
                    </div>

                    <div className="d-flex align-items-center justify-content-center gap-3">
                        <div className="wallet-container position-relative">
                            {showNeededFund ? (
                                <>
                                    <span className="wallet-label">Total Available Funds</span>
                                    <span className="wallet-amount text-danger">
                                        - ₹ {neededFund.toFixed(2)}
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span className="wallet-label">Total Available Funds</span>
                                    <span className="wallet-amount text-success">
                                        ₹ {totalRemainingFund.toFixed(2)}
                                    </span>
                                </>
                            )}
                        </div>
                        {user?.RoleId === VP_ID && (
                            <div className="d-flex align-items-end mb-1 mt-1">
                                <div className="create-btn">
                                    <button
                                        type="button"
                                        className="btn"
                                        onClick={handleAddFundClick}
                                    >
                                        <CreateBillIcon />
                                        <span className="ms-2">Add Fund</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="section-header mt-3 pb-0 bg-white d-flex flex-wrap justify-content-between align-items-center">
                    <div className="col-md-3">
                        <select
                            name="fundFilter"
                            id="fundFilter"
                            className="form-select my-1"
                            value={fundFilter}
                            onChange={handleFundFilterChange}
                        >
                            <option value="">Select Fund</option>
                            {fundsList.map((item, index) => (
                                <option key={index} value={item.FundNo}>
                                    {item.FundNo}
                                </option>
                            ))}
                        </select>
                    </div>
                    {/* {fundsList.length > 0 && ( */}
                    <div className="Export-Icon" onClick={handleExportClick}>
                        <img src={exporticon} alt="" className="cursor-pointer" />
                    </div>
                    {/* )} */}
                </div>

                <div className="form-style pt-2">
                    <div className="pur-item-list table-responsive mb-4 tbl-body-shadow">
                        <table className="w-100 table table-theme-1 mb-0" {...getTableProps()}>
                            <thead className="table-heads">
                                {headerGroups.map(headerGroup => (
                                    <tr {...headerGroup.getHeaderGroupProps()}>
                                        {headerGroup.headers.map(column => (
                                            <th
                                                {...column.getHeaderProps(column.getSortByToggleProps())}
                                                className=""
                                            >
                                                <div className="d-flex">
                                                    {column.render('Header')}
                                                    {column.canSort && (
                                                        <span className="">
                                                            {column.isSorted
                                                                ? column.isSortedDesc
                                                                    ? ' 🔽'
                                                                    : ' 🔼'
                                                                : ' ⬆️⬇️'}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody {...getTableBodyProps()}>
                                {rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={columns.length} className="text-center">
                                            {loading ? 'Loading...' : 'No data found.'}
                                        </td>
                                    </tr>
                                ) : (
                                    rows.map(row => {
                                        prepareRow(row);
                                        return (
                                            <tr {...row.getRowProps()}>
                                                {row.cells.map(cell => {
                                                    const cellClass = "";
                                                    return (
                                                        <td
                                                            {...cell.getCellProps()}
                                                            className={cellClass}
                                                        >
                                                            {cell.render('Cell')}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                            {rows.length > 0 && (
                                <tfoot>
                                    <tr className="total-show">
                                        <td></td>
                                        <td></td>
                                        <td className="text-dark fw-bold" style={{ background: '#ffc809' }}>Total</td>
                                        <td className="text-end text-dark fw-bold text-nowrap" style={{ background: '#ffc809' }}>
                                            ₹ {totalAmount.toFixed(2)}
                                        </td>
                                        <td className="text-end text-dark fw-bold text-nowrap" style={{ background: '#ffc809' }}>
                                            ₹ {balanceAmount.toFixed(2)}
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>

                <div ref={expenseHistoryRef}>
                    {selectedFund && selectedFund.Invoices && (
                        <div className="mt-4">
                            <Loader isLoading={expenseHistoryLoading} />
                            <div className="section-header d-flex justify-content-between align-items-center">
                                <h3>Expense History - ({selectedFund.FundNo})</h3>
                            </div>
                            <div className="form-style">
                                <div className="pur-item-list table-responsive tbl-body-shadow">
                                    <table className="w-100 table table-theme-1 mb-0">
                                        <thead className="table-heads">
                                            <tr>
                                                <th className="">Bill Number</th>
                                                <th className="text-center">Created Date</th>
                                                <th className="text-center">Bill Date</th>
                                                <th className="text-end">Expense Total</th>
                                                <th className="text-end">Related Funds</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedFund.Invoices.map((invoice, idx) => (
                                                <tr key={invoice.InvoiceId}>
                                                    <td className="">
                                                        {invoice.InvoiceId ? (
                                                            <Link
                                                                to={`/view-petty-cash/${invoice.InvoiceId}`}
                                                                target="_blank"
                                                                className="text-primary"
                                                                style={{ textDecoration: 'underline' }}
                                                            >
                                                                {invoice.DocumentNo}
                                                            </Link>
                                                        ) : (
                                                            invoice.DocumentNo || "N/A"
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        {moment(invoice.CreatedAt).format("DD-MM-YYYY")}
                                                    </td>
                                                    <td className="text-center text-nowrap">
                                                        {moment(invoice.BillDate).format("DD-MM-YYYY")}
                                                    </td>
                                                    <td className="text-end">₹ {invoice.InvoiceTotal?.toFixed(2)}</td>
                                                    <td className="text-end">
                                                        {invoice.Funds && invoice.Funds.length > 0 && (
                                                            invoice.Funds.map((fa, i) => (
                                                                <div className="text-nowrap" key={i}>
                                                                    <span className="fundNumber" style={{ fontWeight: fa.FundNo === selectedFundNo ? 'bold' : 'normal' }}>{fa.FundNo}</span>
                                                                    <span style={{ fontWeight: fa.FundNo === selectedFundNo ? 'bold' : 'normal' }}>:</span>
                                                                    <span className="fundAmount" style={{ fontWeight: fa.FundNo === selectedFundNo ? 'bold' : 'normal' }}>₹ {fa.AllocatedAmount?.toFixed(2)}</span>
                                                                </div>
                                                            ))
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className="total-show">
                                                <td></td>
                                                <td></td>
                                                <td className="text-center text-dark fw-bold" style={{ background: '#ffc809' }}>Total</td>
                                                <td className="text-end text-dark fw-bold" style={{ background: '#ffc809' }}>
                                                    ₹ {selectedFund.Invoices.reduce((sum, inv) => sum + parseFloat(inv.InvoiceTotal || 0), 0).toFixed(2)}
                                                </td>
                                                <td className="text-end text-dark fw-bold" style={{ background: '#ffc809' }}>
                                                    ₹ {selectedFund.Invoices.reduce((sum, inv) => {
                                                        return sum + (inv.Funds?.reduce((a, fa) => a + parseFloat(fa.AllocatedAmount || 0), 0) || 0);
                                                    }, 0).toFixed(2)}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default PettyCashFunds;